import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sendInteract } from '../net/interact.js';
import { useDeskStore, type DeskPrompt } from '../state/desk.js';
import { useSessionStore } from '../state/session-store.js';

/**
 * The question shown when pressing E at a desk (ADR-0042): take it, let go of it, or see who has
 * it. Escape, or walking away from the desk, closes it.
 */
export function DeskPanel({ prompt }: { prompt: DeskPrompt }) {
  const { t } = useTranslation();
  const targetId = useSessionStore((s) => s.interactionTarget?.objectId);
  const close = () => useDeskStore.getState().close();

  useEffect(() => {
    if (targetId !== prompt.objectId) useDeskStore.getState().close();
  }, [targetId, prompt.objectId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') useDeskStore.getState().close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const state = prompt.problem === 'taken' ? 'taken' : prompt.state;
  return (
    <div className="hud-box desk-panel" role="dialog" aria-label={t('desk.title')}>
      <strong>{prompt.name}</strong>
      <p role="status">
        {t(`desk.question.${state}`, { name: prompt.holder })}
        {prompt.problem === 'failed' && ` ${t('desk.failed')}`}
      </p>
      <div className="row">
        {state === 'free' && (
          <button type="button" onClick={() => sendInteract(prompt.objectId, 'assign')} autoFocus>
            {t('desk.assign')}
          </button>
        )}
        {state === 'mine' && (
          <button type="button" onClick={() => sendInteract(prompt.objectId, 'release')} autoFocus>
            {t('desk.release')}
          </button>
        )}
        <button type="button" className="secondary" onClick={close}>
          {state === 'taken' ? t('desk.close') : t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
